import { Spin } from "antd"
import { FC, ReactNode, useState } from "react"
import { IShiftSwapsDocsEntity } from "../typings"
import { useAuthStore } from "stores"
import getIdFromKey from "utils/getIdFromKey"
import { Skeleton } from "components/new-stuff/ui/Skeleton"

// ─── Types ───────────────────────────────────────────────────────────────────

interface CollapseTableHeader {
  label: string
  width?: string
}

interface Department {
  id: number | string
  name: string
}

interface ShiftSwapTableProps {
  headers: CollapseTableHeader[]
  departments: Department[]
  isLoading: boolean
  getEmployees: (departmentId: number | string) => any[]
  renderRow: (employee: any) => ReactNode
  emptyMessage?: string
}

// ─── Sub Components ───────────────────────────────────────────────────────────

interface ShiftRowActionsProps {
  status: number
  isLoading: boolean
  onApprove: () => void
  onReject: () => void
  shiftSwap: IShiftSwapsDocsEntity
}

export const ShiftRowActions: FC<ShiftRowActionsProps> = ({
  status,
  isLoading,
  onApprove,
  onReject,
  shiftSwap,
}) => {
  const { user } = useAuthStore((state) => state)

  const nextEntityId = shiftSwap?.nextReviewEntityId

  const nextRoleName = shiftSwap.nextReviewEntityRoleName?.toLowerCase() || ""

  const person_id = Number(getIdFromKey(user?.personKey || ""))

  const matchEntityId = nextEntityId != null && nextEntityId === person_id

  const matchRole =
    nextEntityId == null &&
    user?.userRoles?.some((role) => role.toLowerCase() === nextRoleName)

  const canApprove =
    user?.isPrimaryAdmin === true ||
    user?.isSecondaryAdmin === true ||
    matchEntityId ||
    matchRole

  if (status === 1) {
    return (
      <div className="shrink-0 flex w-[124px] justify-center items-center">
        <p className="text-green-500">Approved</p>
      </div>
    )
  }

  if (status === 2) {
    return (
      <div className="shrink-0 w-[124px] flex justify-center items-center">
        <p className="text-red-500">Rejected</p>
      </div>
    )
  }

  return (
    <div className="shrink-0 flex w-[124px] justify-center items-start flex-col gap-2">
      {canApprove && (
        <p className="font-bold text-sm text-slate-900">
          Approve this transfer?
        </p>
      )}
      <div className="flex gap-2 items-center justify-center">
        {isLoading ? (
          <Spin size="small" />
        ) : (
          canApprove && (
            <>
              <button
                onClick={onApprove}
                className="border border-slate-300 w-10 h-10 rounded-md flex items-center justify-center hover:border-blue-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m4.5 12.75 6 6 9-13.5"
                  />
                </svg>
              </button>
              <button
                onClick={onReject}
                className="border border-slate-300 w-10 h-10 rounded-md flex items-center justify-center hover:border-red-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </>
          )
        )}
      </div>
      <div>
        <p className="font-bold text-sm text-slate-900">
          {shiftSwap.reviewStatus}
        </p>
      </div>
    </div>
  )
}

// ─── Department Section ───────────────────────────────────────────────────────

interface DepartmentSectionProps {
  employees: any[]
  isLoading: boolean
  emptyMessage: string
  renderRow: (employee: any) => ReactNode
}

const DepartmentSection: FC<DepartmentSectionProps> = ({
  employees,
  isLoading,
  emptyMessage,
  renderRow,
}) => {
  return (
    <div className="border-t border-t-gray-200">
      {isLoading ? (
        // Rows at the height they will be, so the list does not collapse to a
        // single grey bar and then expand. The spinner on the approve and
        // reject buttons above stays: that one marks an action in flight,
        // which is what a spinner is for.
        <div className="flex flex-col gap-2 py-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="block"
              width="100%"
              height="94px"
            />
          ))}
        </div>
      ) : !employees.length ? (
        <p className="py-5 flex justify-center items-center">{emptyMessage}</p>
      ) : (
        <div className="overflow-x-auto">{employees.map(renderRow)}</div>
      )}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

const ShiftSwapTable: FC<ShiftSwapTableProps> = ({
  headers,
  departments,
  isLoading,
  getEmployees,
  renderRow,
  emptyMessage = "No data found",
}) => {
  const gridCols = headers.map((h) => h.width || "1fr").join(" ")

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden mt-5">
      {/* Table Header */}
      <ul
        className="grid gap-3 items-center bg-[#F8FAFC] border-b border-gray-300 py-3 px-2 font-semibold overflow-x-auto"
        style={{ gridTemplateColumns: gridCols }}
      >
        {headers.map(({ label }, index) => (
          <li key={`${label}-${index}`}>{label}</li>
        ))}
      </ul>

      {/* Department Sections */}
      {departments?.map((department) => (
        <DepartmentSection
          key={department.id}
          employees={getEmployees(department.id)}
          isLoading={isLoading}
          emptyMessage={emptyMessage}
          renderRow={renderRow}
        />
      ))}
    </div>
  )
}

export default ShiftSwapTable
